import { ClassNamesConfig } from 'react-select'
import { cn } from 'utils/style'

export type SelectVariant = 'default' | 'outline' | 'form' | 'filled'

const defaultClassNames: ClassNamesConfig = {
	option: ({ isDisabled, isFocused, isSelected }) =>
		cn(
			'!cursor-pointer !whitespace-nowrap',
			isDisabled ? '!bg-white !text-dustyGray' : '',
			isSelected || isFocused ? '!bg-dodgerBlue !text-white' : ''
		),
	menu: () => '!z-[10000] !min-w-full !right-0 md:!w-auto',
	control: ({ isFocused }) =>
		cn(
			'!flex !rounded-lg !bg-white !border-none !cursor-pointer',
			'!outline-none',
			'[-webkit-tap-highlight-color:transparent] [user-select:none]',
			isFocused ? '!shadow-[0_0_5px_#465cff]' : '!shadow-[0_0_4px_rgba(0,0,0,0.08)]'
		)
}

const outlineClassNames: ClassNamesConfig = {
	option: ({ isDisabled, isFocused, isSelected }) =>
		cn(
			'!cursor-pointer !whitespace-nowrap !px-3 !py-2 !text-[13px]',
			isDisabled ? '!cursor-not-allowed !text-muted-foreground/60' : '',
			isFocused && !isSelected ? '!bg-muted !text-foreground' : '',
			isSelected ? '!bg-primary !text-primary-foreground' : ''
		),
	menu: () => '!z-[10000] !min-w-full !rounded-lg !border !border-border !shadow-panel',
	menuPortal: () => '!z-[10000]',
	menuList: () => '!p-1',
	placeholder: () => '!text-foreground',
	singleValue: () => '!text-foreground',
	control: ({ isFocused, isDisabled }) =>
		cn(
			'!min-h-[36px] !cursor-pointer !rounded-lg !border !border-border !bg-card !text-[13px] !shadow-none !transition-colors',
			'[-webkit-tap-highlight-color:transparent] [user-select:none]',
			isFocused ? '!border-primary !ring-2 !ring-primary/20' : 'hover:!border-muted-foreground/40',
			isDisabled ? '!cursor-not-allowed !bg-muted' : ''
		),
	dropdownIndicator: () => '!py-0 !pr-2.5 !text-muted-foreground',
	clearIndicator: () => '!py-0 !text-muted-foreground'
}

const tapClassName = '[-webkit-tap-highlight-color:transparent] [user-select:none]'

const formClassNames: ClassNamesConfig = {
	...outlineClassNames,
	placeholder: () => '!text-muted-foreground',
	control: ({ isFocused, isDisabled }) =>
		cn(
			'!min-h-[40px] !cursor-pointer !rounded-md !border !border-border !bg-background !text-sm !shadow-none !transition-colors',
			tapClassName,
			isFocused ? '!border-primary !ring-2 !ring-primary/20' : 'hover:!border-muted-foreground/40',
			isDisabled ? '!cursor-not-allowed !bg-muted' : ''
		),
	valueContainer: () => '!px-3',
	indicatorsContainer: ({ isDisabled }) => (isDisabled ? '!hidden' : ''),
	dropdownIndicator: () => '!py-0 !pl-0 !pr-3 !text-muted-foreground [&>svg]:!h-4 [&>svg]:!w-4',
	clearIndicator: () => '!py-0 !px-1 !text-muted-foreground [&>svg]:!h-4 [&>svg]:!w-4'
}

const filledClassNames: ClassNamesConfig = {
	...outlineClassNames,
	control: ({ isFocused, isDisabled }) =>
		cn(
			'!min-h-[40px] !cursor-pointer !rounded-lg !border !border-border !bg-muted !text-[13px] !shadow-none !transition-colors',
			tapClassName,
			isFocused ? '!ring-2 !ring-primary/20' : '',
			isDisabled ? '!cursor-not-allowed !opacity-60' : ''
		),
	valueContainer: () => '!px-3',
	dropdownIndicator: () =>
		'!py-0 !pl-0 !pr-2.5 !text-muted-foreground [&>svg]:!h-3.5 [&>svg]:!w-3.5',
	clearIndicator: () => '!py-0 !px-1 !text-muted-foreground [&>svg]:!h-3.5 [&>svg]:!w-3.5'
}

export const menuClassNames: ClassNamesConfig = {
	control: ({ isFocused }) =>
		cn(
			'!min-h-0 !cursor-pointer !rounded-md !border !border-border !bg-background !shadow !transition-colors hover:!bg-muted',
			isFocused ? '!ring-2 !ring-primary/20' : ''
		),
	valueContainer: () => '!absolute !h-0 !w-0 !p-0 !opacity-0',
	indicatorsContainer: () => '!hidden',
	menu: () =>
		'!left-auto !right-0 !z-[10000] !w-56 !rounded-lg !border !border-border !bg-card !shadow-panel',
	menuList: () => '!p-1',
	option: ({ isDisabled, isFocused, isSelected }) =>
		cn(
			'!cursor-pointer !rounded-md !px-3 !py-2 !text-[13px]',
			isDisabled ? '!cursor-not-allowed !bg-transparent !text-muted-foreground/60' : '',
			isFocused && !isSelected && !isDisabled ? '!bg-muted !text-foreground' : '',
			isSelected ? '!bg-primary !text-primary-foreground' : ''
		)
}

export const selectClassNames: Record<SelectVariant, ClassNamesConfig> = {
	default: defaultClassNames,
	outline: outlineClassNames,
	form: formClassNames,
	filled: filledClassNames
}
